import { ApolloQueryResult, NetworkStatus, PureQueryOptions, useMutation, useQuery, MutationTuple } from '@apollo/client';

import { GetInventoryDocument, GetHistoricCompletedCoursesDocument, CancelDocument, GetProductDocument } from '@/store/v2';
import { ProviderList } from '@/store/providers';
import { HookResult } from './apolloClient';
import { REFETCH_PROGRESS_CATALOG } from './catalog';
import { CardType, ProductCard } from '@/types/learning/card';
import { EventEnrollment, Inventory, InventoryProduct } from '@/types/learning/learning-catalog';
import { FEATURE, useFeatureEnabled } from '@/feature-toggles';

export interface HistoricCompletedCourse {
  id: number;
  type: string;
  completed: string;
  name: string;
}

const mapInventoryProducts = (item: InventoryProduct) => {
  const { totalEffort, ...restItem } = item;
  const mappedItem: InventoryProduct = restItem;
  if (totalEffort) mappedItem.totalEffort = Number(totalEffort);
  return mappedItem;
};

interface HistoricCompletedCoursesResponse extends HookResult {
  courses: readonly HistoricCompletedCourse[];
  refetch: () => Promise<ApolloQueryResult<{ getHistoricCompletedCourses: HistoricCompletedCourse[] }>>;
}

export const useHistoricCompletedCourses = (): HistoricCompletedCoursesResponse => {
  const { data, error, refetch, networkStatus, loading } = useQuery<{ getHistoricCompletedCourses: HistoricCompletedCourse[] }>(
    GetHistoricCompletedCoursesDocument,
  );
  const courses: ReadonlyArray<HistoricCompletedCourse> = data?.getHistoricCompletedCourses || [];

  return {
    courses,
    error,
    refetch,
    networkStatus,
    loading,
  };
};

interface InventoryResponse extends HookResult {
  inventory: Inventory;
  refetch: () => Promise<
    ApolloQueryResult<{
      getInventory: {
        ongoing: InventoryProduct[];
        completed: InventoryProduct[];
      };
      myEnrollments: EventEnrollment[];
    }>
  >;
}

export const useInventory = (): InventoryResponse => {
  const { data, error, refetch, networkStatus, loading } = useQuery<{
    getInventory: {
      ongoing: InventoryProduct[];
      completed: InventoryProduct[];
    };
    myEnrollments: EventEnrollment[];
  }>(GetInventoryDocument);

  const inventory: Inventory = {
    ongoing: [],
    completed: [],
    eventEnrollments: [],
    allProgress: [],
  };

  if (!(error || loading || !data)) {
    inventory.eventEnrollments = data.myEnrollments;

    inventory.ongoing = (data.getInventory?.ongoing || []).map(mapInventoryProducts);
    inventory.completed = (data.getInventory?.completed || []).map(mapInventoryProducts);
    inventory.allProgress = [...inventory.ongoing, ...inventory.completed];
  }

  return {
    inventory,
    error,
    refetch,
    networkStatus,
    loading: loading && networkStatus === NetworkStatus.loading,
  };
};

export interface CourseCardData {
  extra: CardExtra;
  card: ProductCard;
}

export interface InventoryProductCard extends ProductCard {
  state?: 'registered' | 'pending approval' | 'not approved' | 'approved' | 'cancelled';
  userSelfTrackedLearningId?: number;
  journeyId?: number;
  totalLearningsCount?: number;
}

export interface CardExtra {
  cancelOrder?: () => void;
  loadingCancel?: boolean;
  isOngoing?: boolean;
}

export function inventoryToCourseCards(
  inventory: InventoryProduct[],
  cancelOrder: (params: { variables: { orderId: number } }) => void,
  loadingCancel: boolean,
  isOngoing = false,
  showArchived = true,
): CourseCardData[] {
  const items = inventory.map((item) => {
    const getCardType = (item: InventoryProduct): CardType => {
      if (
        item.label === 'user-generated' ||
        item.label === 'external' ||
        item.label === 'course' ||
        item.label === 'journey' ||
        item.provider === ProviderList.USER_SELF_TRACKED_LEARNING
      ) {
        return item.label as CardType;
      }

      return 'learning-path';
    };

    const card: InventoryProductCard = {
      __typename: 'ProductCard',
      id: `product-${item.id}`,
      entity: 'product',
      entityId: item.productId,
      userSelfTrackedLearningId: item.userSelfTrackedLearningId,
      journeyId: item.journeyId,
      title: item.name,
      teaser: item.teaser,
      subjects: item.subjects,
      image: item.image || null,
      type: getCardType(item),
      totalEffort: item.totalEffort,
      totalLearningsCount: item.totalLearningsCount,
      spaceId: item.spaceId,
      provider: item.provider,
      customLink: item.customLink,
      featured: item.featured,
      participation: item.participation,
      available: item.available,
      state: item.state,
      isExternal: item.isExternal,
      language: null,
      archived: item.archived,
      canEdit: false,
      isApprovalRequired: item.isApprovalRequired,
    };

    const extra: CardExtra = {};
    if (isOngoing) extra.isOngoing = true;
    if (isOngoing && item.isCancellable) {
      // No discernible reason to cancel completed courses
      extra.cancelOrder = () => cancelOrder({ variables: { orderId: item.orderId } });
      extra.loadingCancel = loadingCancel;
    }
    return { card, extra };
  });

  return showArchived ? items : items.filter((m) => !m.card.archived);
}

export const useCancelOrder = (id?: number): MutationTuple<{ getInventory: Inventory }, { orderId: number }> => {
  const isCatalogJSONResponseEnabled = useFeatureEnabled(FEATURE.SYSTEM_CATALOG_GQL_JSON_RESPONSE_ENABLED);
  const refetchQueries: PureQueryOptions[] = [{ query: GetInventoryDocument }];

  if (id) refetchQueries.push({ query: GetProductDocument, variables: { id } });
  else refetchQueries.push(REFETCH_PROGRESS_CATALOG(isCatalogJSONResponseEnabled));

  return useMutation(CancelDocument, {
    refetchQueries,
    awaitRefetchQueries: true,
  });
};
